import { IFilter } from "@saleor/components/Filter";
import { AutocompleteFilterOpts, FilterOpts, MinMax } from "@saleor/types";
import { PaymentChannelCodeEnum } from "@saleor/types/globalTypes";
import {
  createAutocompleteField,
  createDateField,
  createOptionsField
} from "@saleor/utils/filters/fields";
import { defineMessages, IntlShape } from "react-intl";

export enum OrderLinesFilterKeys {
  brand = "brand",
  mall = "mall",
  orderDate = "orderDate",
  completedAt = "completedAt",
  orderStatus = "orderStatus",
  canceledAt = "canceledAt",
  shippingMethodName = "shippingMethodName",
  paymentMethodType = "paymentMethodType"
}

export enum orderStatus {
  newOrder = "new order",
  acknowledged = "acknowledged",
  ready = "ready",
  collected = "collected",
  receivedAtFc = "received at fc",
  packed = "packed",
  readyToShip = "ready to ship",
  shipped = "shipped",
  completed = "completed",
  canceled = "canceled"
}

export enum paymentEnum {
  card = "card",
  cash = "cash",
  gcash = "gcash",
  BPI = "onlinebanking BPI",
  CHINABANK = "onlinebanking CHINABANK",
  RCBC = "onlinebanking RCBC",
  UBP = "onlinebanking UBP",
  GRABPAY = "ewallet GRABPAY",
  PAYMAYA = "ewallet PAYMAYA"
}

export enum deliveryType {
  EXPRESS = "express",
  IN_STORE_PICKUP = "in_store_pickup",
  STANDARD_DELIVERY = "next_day",
  SAME_DAY_DELIVERY = "same_day",
  NATIONWIDE_DELIVERY = "nationwide_delivery"
}
export interface OrderLinesFinanceListFilterOpts {
  orderDate: FilterOpts<MinMax>;
  completedAt: FilterOpts<MinMax>;
  brand: FilterOpts<string[]> & AutocompleteFilterOpts;
  mall: FilterOpts<string[]> & AutocompleteFilterOpts;
  orderStatus: FilterOpts<string[]>;
  shippingMethodName: FilterOpts<string[]>;
  paymentMethodType: FilterOpts<string>;
  canceledAt: FilterOpts<MinMax>;
  channelCode: FilterOpts<PaymentChannelCodeEnum | null>;
}

const messages = defineMessages({
  acknowledged: {
    defaultMessage: "ACKNOWLEDGED",
    description: "acknowledged"
  },
  bpi: {
    defaultMessage: "Online Banking - BPI",
    description: "online banking bpi"
  },
  brand: {
    defaultMessage: "Brand",
    description: "brand"
  },

  canceled: {
    defaultMessage: "CANCELED",
    description: "canceled status"
  },

  canceledAt: {
    defaultMessage: "Order Cancelation Date",
    description: "order cancelation date"
  },
  card: {
    defaultMessage: "CARD",
    description: "card"
  },
  cash: {
    defaultMessage: "CASH",
    description: "cash"
  },
  chinabank: {
    defaultMessage: "Online Banking - CHINABANK",
    description: "online banking chinabank"
  },
  collected: {
    defaultMessage: "COLLECTED",
    description: "collected status"
  },
  completed: {
    defaultMessage: "COMPLETED",
    description: "completed status"
  },
  completedAt: {
    defaultMessage: "Order Completion Date",
    description: "order completetion date"
  },

  express: {
    defaultMessage: "Express",
    description: "express"
  },
  gcash: {
    defaultMessage: "GCASH",
    description: "cash"
  },
  grabpay: {
    defaultMessage: "E-Wallet - GRABPAY",
    description: "e-wallet grabpay"
  },
  inStorePickup: {
    defaultMessage: "In Store Pickup ",
    description: "in store pickup "
  },
  mall: {
    defaultMessage: "Mall",
    description: "mall name"
  },
  nationwideDelivery: {
    defaultMessage: "Nationwide Delivery",
    description: "nationwide delivery"
  },
  newOrder: {
    defaultMessage: "NEW ORDER",
    description: "new order"
  },
  orderDate: {
    defaultMessage: "Order Created Date",
    description: "order created date"
  },
  orderStatus: {
    defaultMessage: "Order Status",
    description: "status"
  },

  packed: {
    defaultMessage: "PACKED",
    description: "packed status"
  },
  paymaya: {
    defaultMessage: "E-Wallet - MAYA",
    description: "e-wallet paymaya"
  },
  paymentMethodType: {
    defaultMessage: "Payment Method",
    description: "payment method"
  },
  rcbc: {
    defaultMessage: "Online Banking - RCBC",
    description: "online banking rcbc"
  },
  ready: {
    defaultMessage: "READY",
    description: "ready"
  },
  readyToShip: {
    defaultMessage: "READY TO SHIP",
    description: "ready to ship status"
  },
  receivedAtFc: {
    defaultMessage: "RECEIVED AT FC",
    description: "received at fc status"
  },
  sameDayDelivery: {
    defaultMessage: "Same Day Delivery",
    description: "same day delivery"
  },
  shipped: {
    defaultMessage: "SHIPPED",
    description: "shipped status"
  },
  shippingMethodName: {
    defaultMessage: "Delivery Type",
    description: "delivery type"
  },
  standardDelivery: {
    defaultMessage: "Standard Delivery",
    description: "standard delivery"
  },
  ubp: {
    defaultMessage: "Online Banking - UBP",
    description: "online banking ubp"
  }
});

export function createFilterStructure(
  intl: IntlShape,
  opts: OrderLinesFinanceListFilterOpts
): IFilter<OrderLinesFilterKeys> {
  return [
    {
      ...createDateField(
        OrderLinesFilterKeys.orderDate,
        intl.formatMessage(messages.orderDate),
        opts.orderDate.value
      ),
      active: opts.orderDate.active
    },
    {
      ...createDateField(
        OrderLinesFilterKeys.completedAt,
        intl.formatMessage(messages.completedAt),
        opts.completedAt.value
      ),
      active: opts.completedAt.active
    },
    {
      ...createDateField(
        OrderLinesFilterKeys.canceledAt,
        intl.formatMessage(messages.canceledAt),
        opts.canceledAt.value
      ),
      active: opts.canceledAt.active
    },
    {
      ...createAutocompleteField(
        OrderLinesFilterKeys.brand,
        intl.formatMessage(messages.brand),
        opts.brand.value,
        opts.brand.displayValues,
        true,
        opts.brand.choices,
        {
          hasMore: opts.brand.hasMore,
          initialSearch: "",
          loading: opts.brand.loading,
          onFetchMore: opts.brand.onFetchMore,
          onSearchChange: opts.brand.onSearchChange
        }
      ),
      active: opts.brand.active
    },
    {
      ...createAutocompleteField(
        OrderLinesFilterKeys.mall,
        intl.formatMessage(messages.mall),
        opts.mall.value,
        opts.mall.displayValues,
        true,
        opts.mall.choices,
        {
          hasMore: opts.mall.hasMore,
          initialSearch: "",
          loading: opts.mall.loading,
          onFetchMore: opts.mall.onFetchMore,
          onSearchChange: opts.mall.onSearchChange
        }
      ),
      active: opts.mall.active
    },
    {
      ...createOptionsField(
        OrderLinesFilterKeys.orderStatus,
        intl.formatMessage(messages.orderStatus),
        opts.orderStatus.value,
        true,
        [
          {
            label: intl.formatMessage(messages.newOrder),
            value: orderStatus.newOrder
          },
          {
            label: intl.formatMessage(messages.acknowledged),
            value: orderStatus.acknowledged
          },
          {
            label: intl.formatMessage(messages.ready),
            value: orderStatus.ready
          },
          {
            label: intl.formatMessage(messages.collected),
            value: orderStatus.collected
          },
          {
            label: intl.formatMessage(messages.receivedAtFc),
            value: orderStatus.receivedAtFc
          },
          {
            label: intl.formatMessage(messages.packed),
            value: orderStatus.packed
          },
          {
            label: intl.formatMessage(messages.readyToShip),
            value: orderStatus.readyToShip
          },
          {
            label: intl.formatMessage(messages.shipped),
            value: orderStatus.shipped
          },
          {
            label: intl.formatMessage(messages.completed),
            value: orderStatus.completed
          },
          {
            label: intl.formatMessage(messages.canceled),
            value: orderStatus.canceled
          }
        ]
      ),
      active: opts.orderStatus.active
    },
    {
      ...createOptionsField(
        OrderLinesFilterKeys.shippingMethodName,
        intl.formatMessage(messages.shippingMethodName),
        opts.shippingMethodName.value,
        true,
        [
          {
            label: intl.formatMessage(messages.express),
            value: deliveryType.EXPRESS
          },
          {
            label: intl.formatMessage(messages.standardDelivery),
            value: deliveryType.STANDARD_DELIVERY
          },
          {
            label: intl.formatMessage(messages.inStorePickup),
            value: deliveryType.IN_STORE_PICKUP
          },
          {
            label: intl.formatMessage(messages.sameDayDelivery),
            value: deliveryType.SAME_DAY_DELIVERY
          },
          {
            label: intl.formatMessage(messages.nationwideDelivery),
            value: deliveryType.NATIONWIDE_DELIVERY
          }
        ]
      ),
      active: opts.shippingMethodName.active
    },
    {
      ...createOptionsField(
        OrderLinesFilterKeys.paymentMethodType,
        intl.formatMessage(messages.paymentMethodType),
        [opts.paymentMethodType.value],
        false,
        [
          {
            label: intl.formatMessage(messages.card),
            value: paymentEnum.card
          },
          {
            label: intl.formatMessage(messages.cash),
            value: paymentEnum.cash
          },
          {
            label: intl.formatMessage(messages.gcash),
            value: paymentEnum.gcash
          },
          {
            label: intl.formatMessage(messages.bpi),
            value: paymentEnum.BPI
          },
          {
            label: intl.formatMessage(messages.chinabank),
            value: paymentEnum.CHINABANK
          },
          {
            label: intl.formatMessage(messages.rcbc),
            value: paymentEnum.RCBC
          },
          {
            label: intl.formatMessage(messages.ubp),
            value: paymentEnum.UBP
          },
          {
            label: intl.formatMessage(messages.grabpay),
            value: paymentEnum.GRABPAY
          },
          {
            label: intl.formatMessage(messages.paymaya),
            value: paymentEnum.PAYMAYA
          }
        ]
      ),
      active: opts.shippingMethodName.active
    }
  ];
}
